import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { createLead } from "@/lib/api";

export const WhatsAppIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.895 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const EASE = [0.22, 1, 0.36, 1];

export const Contact = () => {
  const { content, t, lang } = useSite();
  const c = content.contact;
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const onSubmitLead = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(lang === "zh" ? "请填写姓名和电话" : "Please fill in your name and phone");
      return;
    }
    setSending(true);
    try {
      await createLead(form);
      setForm({ name: "", phone: "", message: "" });
      toast.success(lang === "zh" ? "已收到您的留言，我们会尽快联系您！" : "Message received! We'll reach out soon.");
    } catch {
      toast.error(lang === "zh" ? "提交失败，请稍后再试" : "Submit failed, please try again");
    } finally {
      setSending(false);
    }
  };

  const items = [
    { icon: Phone, label: lang === "zh" ? "电话" : "Phone", value: c.phone, href: `tel:${c.phone.replace(/[^+\d]/g, "")}`, testid: "contact-phone" },
    { icon: Mail, label: lang === "zh" ? "邮箱" : "Email", value: c.email, href: `mailto:${c.email}`, testid: "contact-email" },
    { icon: WhatsAppIcon, label: "WhatsApp", value: c.whatsapp, href: `https://wa.me/${(c.whatsapp || "").replace(/\D/g, "")}`, testid: "contact-whatsapp" },
    { icon: MapPin, label: lang === "zh" ? "地址" : "Address", value: t(c, "address"), testid: "contact-address" },
    { icon: Clock, label: lang === "zh" ? "服务时间" : "Hours", value: t(c, "hours"), testid: "contact-hours" },
  ];

  return (
    <footer id="contact" data-testid="contact-section" className="relative bg-sky-400 overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-orange-400/30 blur-3xl translate-x-1/3 -translate-y-1/3" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-900/70">
              {lang === "zh" ? "联系我们" : "Get in Touch"}
            </p>
            <h2 className="mt-4 font-display font-black tracking-tighter leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-slate-900">
              {lang === "zh" ? "今天，就为明天撑把伞" : "Open an umbrella today,"}
              <br />
              <span className="text-white">{lang === "zh" ? "从一次聊聊开始。" : "for tomorrow's rain."}</span>
            </h2>
            <a
              data-testid="contact-cta-button"
              href={`mailto:${c.email}`}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-sm font-bold text-white hover:bg-orange-500 hover:-translate-y-0.5 transition-[background-color,transform] duration-300 shadow-xl"
            >
              <Mail size={16} />
              {lang === "zh" ? "发邮件给我们" : "Email Us"}
            </a>
          </motion.div>

          <motion.form
            data-testid="lead-form"
            onSubmit={onSubmitLead}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="rounded-3xl bg-white p-8 shadow-2xl space-y-4"
          >
            <h3 className="font-display font-bold text-xl text-slate-900">
              {lang === "zh" ? "预约免费咨询" : "Book a Free Consultation"}
            </h3>
            <p className="text-sm text-slate-500">
              {lang === "zh" ? "留下您的联系方式，顾问将在 1 个工作日内回复。" : "Leave your details and an advisor will reply within 1 business day."}
            </p>
            <input
              data-testid="lead-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={lang === "zh" ? "您的姓名 *" : "Your name *"}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-[border-color,box-shadow] duration-300"
            />
            <input
              data-testid="lead-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder={lang === "zh" ? "电话 / WhatsApp *" : "Phone / WhatsApp *"}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-[border-color,box-shadow] duration-300"
            />
            <textarea
              data-testid="lead-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={lang === "zh" ? "想咨询的内容（可选）" : "What would you like to ask? (optional)"}
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-[border-color,box-shadow] duration-300 resize-none"
            />
            <button
              data-testid="lead-submit-button"
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-6 py-3.5 text-sm font-bold text-white hover:bg-orange-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-300 disabled:opacity-60"
            >
              <Send size={15} />
              {sending ? (lang === "zh" ? "提交中…" : "Sending…") : (lang === "zh" ? "提交，等顾问联系我" : "Submit — an advisor will call me")}
            </button>
          </motion.form>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="mt-20 grid sm:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          {items.map(({ icon: Icon, label, value, href, testid }) => {
            const inner = (
              <>
                <Icon size={20} className="text-orange-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900 break-words">{value}</p>
                </div>
              </>
            );
            return href ? (
              <a key={testid} data-testid={testid} href={href} className="flex items-start gap-3 rounded-2xl bg-white/80 backdrop-blur-sm p-5 hover:bg-white transition-colors duration-300">
                {inner}
              </a>
            ) : (
              <div key={testid} data-testid={testid} className="flex items-start gap-3 rounded-2xl bg-white/80 backdrop-blur-sm p-5">
                {inner}
              </div>
            );
          })}
        </motion.div>

        <div className="mt-20 pt-8 border-t border-slate-900/10 flex flex-wrap items-center justify-between gap-4">
          <p className="flex items-center gap-2.5 font-display font-extrabold text-slate-900">
            <img src="/logo.png" alt="i.S.i Group" className="h-9 w-auto object-contain" />
            i.S.i Group
          </p>
          <p className="text-xs font-semibold text-slate-900/50">
            © {new Date().getFullYear()} i.S.i Group. {lang === "zh" ? "用心守护每个家。" : "Guarding every family with care."}
          </p>
        </div>
      </div>
    </footer>
  );
};
